import { useState, useRef, useEffect } from 'react';

export function useDraggable(
  initialPos: { top: number; left: number },
  isOpen?: boolean,
  containerRef?: React.RefObject<HTMLElement | null>
) {
  const [pos, setPos] = useState(initialPos);
  const dragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0, top: 0, left: 0 });
  const prevIsOpen = useRef(isOpen);
  const currentPos = useRef(initialPos);

  // When panel transitions from closed (false) to open (true), reset to initial default position
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      setPos(initialPos);
      currentPos.current = initialPos;
      if (containerRef && containerRef.current) {
        containerRef.current.style.top = `${initialPos.top}px`;
        containerRef.current.style.left = `${initialPos.left}px`;
      }
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, initialPos, containerRef]);

  // When sidebar expands/collapses, reset position to updated default initialPos
  useEffect(() => {
    setPos(initialPos);
    currentPos.current = initialPos;
    if (containerRef && containerRef.current) {
      containerRef.current.style.top = `${initialPos.top}px`;
      containerRef.current.style.left = `${initialPos.left}px`;
    }
  }, [initialPos.left, initialPos.top, containerRef]);

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      top: currentPos.current.top,
      left: currentPos.current.left
    };
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    const nextTop = startPos.current.top + dy;
    const nextLeft = startPos.current.left + dx;

    currentPos.current = { top: nextTop, left: nextLeft };

    if (containerRef && containerRef.current) {
      containerRef.current.style.top = `${nextTop}px`;
      containerRef.current.style.left = `${nextLeft}px`;
    } else {
      setPos({ top: nextTop, left: nextLeft });
    }
  };

  const onPointerUp = () => {
    dragging.current = false;
    if (containerRef && containerRef.current) {
      setPos(currentPos.current);
    }
  };

  return {
    pos,
    setPos,
    style: { position: 'absolute' as const, top: pos.top, left: pos.left },
    headerProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      style: { cursor: 'move', userSelect: 'none' as const },
    },
  };
}
